import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { CheckCircle2, XCircle, HelpCircle, Info, Loader2, Stethoscope } from 'lucide-react';
import { readinessApi } from '../lib/api';

/*
 * A4: "can this slot achieve goal G right now?" — the gap engine's report, rendered the way the
 * spec's conversation shape demands: BLOCKERS FIRST, never a wall of green checks.
 *
 * The statuses are the server's honesty contract, kept visually distinct on purpose:
 *   fail    = a requirement is definitely not met (red)
 *   unknown = could not tell (amber) — e.g. no diagnostic snapshot uploaded for the device yet.
 *             Unknown is NEVER counted as ready.
 *   pass    = verified met (green)
 *   info    = no automated check exists (grey) — surfaced for a human, not a verdict
 *
 * Aurora (2026-08-23): the colours are the semantic tokens (danger / warning / success / muted)
 * so the dialog reads like the rest of the product; the semantics above are unchanged.
 */

const STATUS = {
  fail: { icon: XCircle, cls: 'text-danger', badge: 'bg-danger/15 text-danger ring-1 ring-danger/25' },
  unknown: { icon: HelpCircle, cls: 'text-warning', badge: 'bg-warning/15 text-warning ring-1 ring-warning/25' },
  pass: { icon: CheckCircle2, cls: 'text-success', badge: 'bg-success/15 text-success ring-1 ring-success/25' },
  info: { icon: Info, cls: 'text-muted-foreground', badge: 'bg-muted/60 text-muted-foreground ring-1 ring-border' },
};

export default function ReadinessDialog({ slotName, open, onOpenChange }) {
  const [goal, setGoal] = useState('');

  const goalsQuery = useQuery({
    queryKey: ['readiness-goals'],
    queryFn: () => readinessApi.goals(),
    enabled: open,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  const evalQuery = useQuery({
    queryKey: ['readiness', slotName, goal],
    queryFn: () => readinessApi.evaluate(slotName, goal),
    enabled: open && !!goal,
    retry: false,
  });

  const goals = goalsQuery.data?.data || [];
  const result = evalQuery.data?.data;
  const disabledMsg = goalsQuery.isError
    ? (goalsQuery.error?.response?.data?.error || goalsQuery.error?.message)
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl border-border bg-card text-foreground">
        <DialogHeader className="text-left">
          {/* pr-6: keep the title clear of the absolute close button on phones */}
          <DialogTitle className="flex min-w-0 items-center gap-2 pr-6 font-heading text-foreground">
            <Stethoscope className="h-5 w-5 shrink-0 text-primary" />
            <span className="min-w-0 break-words">Readiness — {slotName}</span>
          </DialogTitle>
          <DialogDescription className="text-pretty">
            Deterministic check of everything a goal needs: this slot's settings plus the latest
            diagnostic snapshot of its device. Nothing is changed by running it.
          </DialogDescription>
        </DialogHeader>

        {disabledMsg ? (
          <div className="rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning break-words">
            {disabledMsg}
          </div>
        ) : (
          <div className="space-y-4">
            <Select value={goal} onValueChange={setGoal}>
              <SelectTrigger className="tap bg-secondary/60 border-border text-foreground" data-testid="goal-select">
                <SelectValue placeholder="What do you want this slot to do?" />
              </SelectTrigger>
              <SelectContent className="border-border max-h-72">
                {goals.map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {evalQuery.isFetching && (
              <div className="flex items-center gap-2 text-primary text-sm" aria-busy="true">
                <Loader2 className="h-4 w-4 animate-spin" /> Checking…
              </div>
            )}
            {evalQuery.isError && goal && !evalQuery.isFetching && (
              <div className="rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger break-words" role="alert">
                {evalQuery.error?.response?.data?.error || evalQuery.error?.message}
              </div>
            )}

            {result && !evalQuery.isFetching && (
              <>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm" data-testid="readiness-summary">
                  {result.ready ? (
                    <Badge variant="outline" className="rounded-full border-success/25 bg-success/10 text-success">READY</Badge>
                  ) : (
                    <Badge variant="outline" className="rounded-full border-danger/25 bg-danger/10 text-danger tnum">
                      {result.failed} blocker{result.failed === 1 ? '' : 's'}
                      {result.unknown > 0 ? ` · ${result.unknown} unknown` : ''}
                    </Badge>
                  )}
                  <span className="text-muted-foreground tnum">
                    {result.passed} met · {result.informational} informational
                  </span>
                  {/* the snapshot note takes its own line on phones so a long device id never squeezes the counts */}
                  <span className="w-full min-w-0 break-words text-xs text-muted-foreground sm:w-auto">
                    {result.snapshot.present
                      ? `device snapshot: ${result.snapshot.deviceId}${result.snapshot.stale ? ' (STALE)' : ''}`
                      : `no device snapshot — ${result.snapshot.note || 'run Diagnose in the desktop app'}`}
                  </span>
                </div>

                <div className="divide-y divide-border/70 overflow-hidden rounded-xl border border-border" data-testid="readiness-rows">
                  {result.requirements.map((r) => {
                    const S = STATUS[r.status] || STATUS.info;
                    const Icon = S.icon;
                    return (
                      <div key={r.id} className="flex gap-3 px-3 py-2.5 sm:px-4">
                        <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${S.cls}`} aria-hidden />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-foreground break-words">
                            {r.why || r.id}
                            {r.certainty === 'conditional' && (
                              <span className="text-muted-foreground"> (only in some cases)</span>
                            )}
                          </div>
                          {r.detail && <div className="text-xs text-muted-foreground mt-0.5 break-words">{r.detail}</div>}
                          {(r.settings || []).length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {r.settings.map((s2) => (
                                <span key={`${r.id}-${s2.number}-${s2.field}`}
                                      className="max-w-full break-all text-[11px] font-mono bg-secondary/60 rounded px-1.5 py-0.5 text-foreground/90">
                                  {s2.number != null ? `#${s2.number} ` : ''}{s2.label || s2.field}
                                  {s2.value != null ? ` = ${s2.value}` : ''}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
